import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
  History,
  Layers,
  Loader2,
  Trash2,
} from "lucide-react";
import { ImageModal } from "./ImageModal";
import { API_URL, BACKEND_URL } from "./backendUrl";

const API = API_URL;

const STATUS_BADGES = {
  completed: { label: "TERMINÉ", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  partial: { label: "PARTIEL", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  failed: { label: "ÉCHEC", cls: "bg-red-50 text-red-600 border-red-200" },
  running: { label: "EN COURS", cls: "bg-orange-50 text-[#f58207] border-orange-200" },
  pending: { label: "INITIALISATION", cls: "bg-orange-50 text-[#f58207] border-orange-200" },
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });

const downloadBlob = async (url, filename) => {
  try {
    const res = await fetch(url, { credentials: "include" });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    toast.error("Échec du téléchargement");
  }
};

const DeclinationRuns = ({ jobId, onImageClick }) => {
  const [runs, setRuns] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/jobs/${jobId}/declinations`)
      .then((res) => setRuns(res.data))
      .catch(() => setRuns([]));
  }, [jobId]);

  if (runs === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-400 font-mono py-2">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> chargement des déclinaisons...
      </div>
    );
  }
  if (runs.length === 0) {
    return <p className="text-xs text-slate-400 italic py-2">Aucune déclinaison générée pour ce job.</p>;
  }

  return (
    <div className="space-y-3">
      {runs.map((run) => {
        const badge = STATUS_BADGES[run.status] || STATUS_BADGES.pending;
        const results = run.results || [];
        const categories = [...new Set(results.map((r) => r.category))];
        return (
          <div
            key={run.id}
            className="border border-slate-200 rounded-lg p-3 bg-slate-50/60"
            data-testid={`declination-run-${run.id}`}
          >
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <Layers className="w-3.5 h-3.5 text-[#f58207]" />
                <span className="font-mono text-slate-500">{fmtDate(run.created_at)}</span>
                <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold ${badge.cls}`}>
                  {badge.label}
                </span>
                <span className="font-mono text-slate-600">
                  {results.length}/{run.total} fichiers
                </span>
              </div>
              {results.length > 0 && (
                <button
                  data-testid={`history-declination-zip-${run.id}`}
                  onClick={() =>
                    downloadBlob(`${API}/declinations/${run.id}/zip`, `declinaisons_${run.id.slice(0, 8)}.zip`)
                  }
                  className="h-7 px-2.5 bg-slate-900 text-white text-[11px] font-bold rounded flex items-center gap-1.5 hover:bg-black transition-colors"
                >
                  <Download className="w-3 h-3" /> ZIP
                </button>
              )}
            </div>
            {categories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {categories.map((c) => (
                  <span
                    key={c}
                    className="text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 uppercase"
                  >
                    {c.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
            {results.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-2">
                {results.map((r, i) => {
                  return (
                    <button
                      key={`${run.id}:${r.result_index ?? i}`}
                      type="button"
                      onClick={() => onImageClick?.(r)}
                      className="text-left bg-white border border-slate-200 rounded-lg p-2.5 hover:border-[#f58207] hover:shadow-md transition-all"
                    >
                      <img
                        src={`${BACKEND_URL}${r.url}`}
                        alt={r.title || r.format_name}
                        title={`${r.format_name} · ${r.width}×${r.height}`}
                        className="h-24 w-full object-cover rounded border border-slate-200"
                      />
                      <div className="mt-2">
                        <p className="text-xs font-semibold text-slate-900 truncate">{r.format_name}</p>
                        <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                          <span>{r.category}</span>
                          <span>{r.width}×{r.height}</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const JobCard = ({ job, onDecline, onOpenVariations, onDeleted, onImageClick }) => {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [titleDraft, setTitleDraft] = useState(job.title || job.prompt || "Mission sans titre");
  const badge = STATUS_BADGES[job.status] || STATUS_BADGES.pending;
  const images = job.images || [];
  const galleryImages = [
    {
      index: 0,
      url: job.source_url,
      label: "Image originale",
      filename: job.source_url?.split("/").pop() || "source.png",
      isOriginal: true,
    },
    ...images,
  ];
  const canDecline = images.length > 0 || !!job.source_url;

  const saveTitle = async () => {
    const next = titleDraft.trim();
    if (!next || next === (job.title || job.prompt || "")) return;
    try {
      const res = await axios.patch(`${API}/jobs/${job.id}/title`, { title: next });
      setTitleDraft(res.data.title || next);
      toast.success("Titre mis à jour");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de modifier le titre");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/jobs/${job.id}`);
      toast.success("Suppression effectuée");
      onDeleted(job.id);
    } catch {
      toast.error("Échec de la suppression");
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 card-reveal" data-testid={`history-job-card-${job.id}`}>
      <div className="flex gap-4">
        <img
          src={`${BACKEND_URL}${job.source_url}`}
          alt="source"
          className="w-20 h-20 rounded-lg object-cover border border-slate-200 shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded border text-[10px] font-bold ${badge.cls}`}>{badge.label}</span>
            <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {fmtDate(job.created_at)}
            </span>
          </div>
          <input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
            className="w-full bg-transparent text-sm text-slate-900 font-semibold outline-none border-b border-transparent focus:border-[#f58207] pb-0.5"
            placeholder="Titre de la mission"
          />
          <p className="text-[11px] font-mono text-slate-500 mt-1">
            {images.length}/{job.iterations} variations · {job.declination_runs || 0} série(s) de déclinaisons ·{" "}
            {job.declination_files || 0} fichier(s) décliné(s)
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-slate-100">
        <button
          data-testid={`expand-job-button-${job.id}`}
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-[#f58207] transition-colors"
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {expanded ? "Masquer les détails" : "Voir variations & déclinaisons"}
        </button>
        <div className="flex items-center gap-2">
          {images.length > 0 && (
            <button
              data-testid={`history-job-zip-${job.id}`}
              onClick={() => downloadBlob(`${API}/jobs/${job.id}/zip`, `variations_${job.id.slice(0, 8)}.zip`)}
              className="h-8 px-3 bg-slate-900 text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 hover:bg-black transition-colors"
            >
              <Download className="w-3 h-3" /> ZIP variations
            </button>
          )}
          {canDecline && (
            <>
              <button
                data-testid={`history-decline-button-${job.id}`}
                onClick={() => onDecline(job)}
                className="h-8 px-3 bg-[#f58207] text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 hover:bg-[#c86b05] transition-colors"
              >
                Décliner <ArrowRight className="w-3 h-3" />
              </button>
              <button
                data-testid={`history-variations-button-${job.id}`}
                onClick={() => onOpenVariations(job)}
                className="h-8 px-3 bg-slate-900 text-white text-[11px] font-bold rounded-md flex items-center gap-1.5 hover:bg-black transition-colors"
              >
                Variations <Layers className="w-3 h-3" />
              </button>
            </>
          )}
          <button
            data-testid={`delete-job-button-${job.id}`}
            onClick={handleDelete}
            disabled={deleting}
            className={`h-8 px-3 text-[11px] font-bold rounded-md flex items-center gap-1.5 border transition-colors disabled:opacity-50 ${
              confirmDelete
                ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                : "bg-white text-red-500 border-red-200 hover:bg-red-50"
            }`}
          >
            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            {confirmDelete ? "Confirmer ?" : "Supprimer"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {images.length > 0 && (
            <div>
              <p className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-2">Variations générées</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                {galleryImages.map((img) => (
                  <button
                    key={img.index}
                    data-testid={`history-variation-${job.id}-${img.index}`}
                    onClick={() => onImageClick(img)}
                    className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-[#f58207] hover:shadow-md transition-all group"
                  >
                    <img
                      src={`${BACKEND_URL}${img.url}`}
                      alt={img.isOriginal ? "Image originale" : `v${img.index}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-1 left-1 bg-white/90 text-[9px] font-bold px-1 py-0.5 rounded text-slate-900">
                      {img.isOriginal ? "Originale" : `#${img.index}`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] text-slate-500 uppercase mb-2">Déclinaisons</p>
            <DeclinationRuns
              jobId={job.id}
              onImageClick={(img) => onImageClick?.(img)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default function HistoryPage({ onBack, onDecline, onOpenVariations, onLogout }) {
  const [jobs, setJobs] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  const openHistoryImage = (job, img) => {
    setModalImage(img);
    setActiveJob(job);
  };

  useEffect(() => {
    axios
      .get(`${API}/jobs`)
      .then((res) => setJobs(res.data))
      .catch(() => {
        setJobs([]);
        toast.error("Impossible de charger l'historique");
      });
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 font-body text-slate-700">
      {modalImage && (
        <ImageModal
          image={modalImage}
          onClose={() => {
            setModalImage(null);
            setActiveJob(null);
          }}
          onDecline={activeJob ? () => onDecline(activeJob) : undefined}
        />
      )}

      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4 sticky top-0 z-20">
        <button
          data-testid="history-back-button"
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#f58207] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux variations
        </button>
        <div className="h-6 w-px bg-slate-200" />
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg overflow-hidden bg-white border border-slate-200 shadow-sm flex items-center justify-center">
            <img src="/logo-gm.png" alt="" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-base font-bold tracking-tight text-slate-900 font-heading">
              Historique des générations
            </h1>
            <p className="text-[11px] text-slate-500 font-mono">
              {jobs === null ? "chargement..." : `${jobs.length} mission(s) archivée(s)`}
            </p>
          </div>
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            className="ml-auto text-xs font-medium text-slate-500 hover:text-red-500 transition-colors"
          >
            Déconnexion
          </button>
        )}
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-4" data-testid="history-jobs-list">
        {jobs === null ? (
          <div className="h-48 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm font-mono">Chargement de l'historique...</span>
          </div>
        ) : jobs.length === 0 ? (
          <div className="h-64 border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center text-slate-400 gap-3" data-testid="history-empty-state">
            <History className="w-10 h-10" />
            <p className="text-sm">Aucune mission dans l'historique pour le moment</p>
          </div>
        ) : (
          jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onDecline={onDecline}
              onOpenVariations={onOpenVariations}
              onImageClick={(img) => openHistoryImage(job, img)}
              onDeleted={(id) => setJobs(jobs.filter((j) => j.id !== id))}
            />
          ))
        )}
      </main>
    </div>
  );
}
